import getUrlParameter from '@rsos/base-utils/getUrlParameter';
import { SCORPIUS_KEY, PRODUCT_TOKEN } from '../../actions/types';

/**
 * Backwards-compatible mitigation for legacy auto-login links that still pass
 * `access_token` in the query string.
 *
 * We intentionally keep consuming that token so existing entry points do not
 * break, but we immediately remove only the sensitive `access_token` parameter
 * from the visible URL after it has been read. This reduces accidental token
 * disclosure through copied links, screenshots, browser history, and referrer
 * headers while preserving the current login flow. Other query params and the
 * hash fragment are preserved.
 */
const stripAccessTokenFromUrl = () => {
  if (
    typeof window === 'undefined' ||
    typeof window.history?.replaceState !== 'function'
  ) {
    return;
  }

  const url = new URL(window.location.href);

  if (!url.searchParams.has('access_token')) {
    return;
  }

  url.searchParams.delete('access_token');

  // Security context: the token may arrive through a legacy deep link, but it
  // should not remain in the address bar any longer than necessary because URL
  // tokens are easy to leak outside the app boundary.
  window.history.replaceState(
    {},
    '',
    `${url.pathname}${url.search}${url.hash}`,
  );
};

/**
 * Returns the Scorpius tokens from localStorage.
 * Existing stored tokens still take precedence. When storage is empty, legacy
 * auto-login links may bootstrap auth from `?access_token=...`; in that case
 * the token is consumed once and then scrubbed from the URL as a defense-in-
 * depth measure against URL-based credential exposure.
 * @returns {Object} tokens
 */
/**
 * Marks tokens that came from a `?access_token=` link rather than a login.
 *
 * Recorded at the moment of the bootstrap because that is the only moment it is
 * knowable: the query parameter is stripped from the URL immediately after, and
 * from then on such a session is indistinguishable in storage from an ordinary
 * one that lost its refresh token. The difference matters — one is working as
 * designed and the other is a fault — and it decides whether running out of
 * access token is reported to Sentry.
 */
export const AUTH_SOURCE_URL_ACCESS_TOKEN = 'url-access-token';

export const getTokens = () => {
  let tokenString = localStorage.getItem(SCORPIUS_KEY);
  const accessToken = getUrlParameter('access_token');
  if (!tokenString && accessToken) {
    const bootstrapTokens = {
      refresh_token: null,
      token: accessToken,
      auth_source: AUTH_SOURCE_URL_ACCESS_TOKEN,
    };
    tokenString = JSON.stringify(bootstrapTokens);
    // Persist the bootstrap token immediately because getTokens() runs while
    // the Redux store initializes, before app-level initialize() handlers run.
    // Without this, stripping the query param can leave downstream apps with
    // no token source by the time they call fetchUser().
    localStorage.setItem(SCORPIUS_KEY, tokenString);
    stripAccessTokenFromUrl();
  }
  if (!tokenString) {
    return null;
  }
  try {
    return JSON.parse(tokenString);
  } catch (error) {
    localStorage.removeItem(SCORPIUS_KEY);
    return null;
  }
};

/**
 * Whether this session was bootstrapped from a `?access_token=` link — IRP and
 * the legacy auto-login deep links. Such a session is issued no refresh token
 * on purpose, so reaching the end of its access token is expected rather than
 * broken.
 *
 * A session that later obtains real credentials is written through `setTokens`,
 * which replaces the stored object wholesale and so drops this marker. That is
 * the intent: once there is a refresh token, none of this applies.
 * @returns {boolean}
 */
export const isBootstrappedSession = () =>
  getTokens()?.auth_source === AUTH_SOURCE_URL_ACCESS_TOKEN;

/**
 * Whether a stored refresh_token value can actually be used to refresh.
 *
 * IRP sessions and legacy `?access_token=` auto-login links have no refresh
 * token at all, and the bootstrap above stores that as `null`. Some older
 * writes also left the string `'null'` behind. Sending any of these to the
 * refresh endpoint produces a 400, so callers must check before refreshing
 * rather than after.
 * @param {unknown} refreshToken
 * @returns {refreshToken is string}
 */
export const isUsableRefreshToken = refreshToken =>
  typeof refreshToken === 'string' &&
  refreshToken !== '' &&
  refreshToken !== 'null';

/**
 * Saves the Scorpius tokens to localStorage.
 * @param {Object} tokens
 * @param {string} tokens.token
 * @param {string} tokens.refresh_token
 */
export const setTokens = tokens => {
  localStorage.setItem(SCORPIUS_KEY, JSON.stringify(tokens));
};

/**
 * Deletes the tokens from localStorage.
 */
export const clearTokens = () => {
  localStorage.removeItem(SCORPIUS_KEY);
  localStorage.removeItem(PRODUCT_TOKEN);
};
